import { useCallback, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import { getApiClient } from '@signalvote/api-client';
import {
  WalkbookStatus,
  type WalkbookDetailResponse,
  type WalkbookListItem,
  type WalkbookResponse,
  type HouseDetailResponse,
  type PolygonPreviewResponse,
  type PolygonUpdateResponse,
  type AssignmentsResponse,
} from '@signalvote/shared';
import { defineQuery, requireSelectedCampaign } from '@/lib/data';

/**
 * Active walkbooks, for the shift dropdowns. Every walkbook read below declares its GET's reads, so a
 * write naming a walkbook, house, assignment or (for the lists) shift refreshes it with no key listed.
 */
export const useWalkbooks = defineQuery({
  key: () => ['walkbooks', 'list', { status: WalkbookStatus.Active }],
  reads: 'GET /api/Walkbooks',
  fetch: async (_: undefined, { campaignId }): Promise<WalkbookListItem[]> => {
    requireSelectedCampaign(campaignId);
    return (await getApiClient().walkbooks.listAll({ status: WalkbookStatus.Active })).items;
  },
  staleTime: 60 * 1000,
});

/** Active walkbooks with their polygons, for the map and the turf list. */
export const useWalkbooksWithPolygons = defineQuery({
  key: () => ['walkbooks', 'list', { status: WalkbookStatus.Active, includePolygon: true }],
  reads: 'GET /api/Walkbooks',
  fetch: async (_: undefined, { campaignId }): Promise<WalkbookListItem[]> => {
    requireSelectedCampaign(campaignId);
    return (await getApiClient().walkbooks.listAll({ status: WalkbookStatus.Active, includePolygon: true })).items;
  },
  staleTime: 60 * 1000,
});

/** One walkbook with its houses, assignments and polygon. */
export const useWalkbookDetail = defineQuery({
  key: (id: string) => ['walkbooks', 'detail', id],
  reads: 'GET /api/Walkbooks/{id}',
  fetch: (id, { campaignId }): Promise<WalkbookDetailResponse> => {
    requireSelectedCampaign(campaignId);
    return getApiClient().walkbooks.getWalkbook(id);
  },
  staleTime: 30 * 1000,
});

export const useWalkbookHouses = defineQuery({
  key: (id: string) => ['walkbooks', 'houses', id],
  reads: 'GET /api/Walkbooks/{id}/houses',
  fetch: async (id, { campaignId }): Promise<HouseDetailResponse[]> => {
    requireSelectedCampaign(campaignId);
    return (await getApiClient().walkbooks.getHouses(id, { pageSize: 1000 })).items;
  },
  staleTime: 30 * 1000,
});

// ============================================================================
// Mutations
// ============================================================================

/**
 * Create a new walkbook from a polygon
 */
export function useCreateWalkbook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; polygon: string }): Promise<WalkbookResponse> => {
      const api = getApiClient();
      return api.walkbooks.createWalkbook(data);
    },
    // no-data-door: the contact-rate overlay is not a defined query yet, so no write set reaches it
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.walkbooks.contactRates() }),
  });
}

/**
 * Archive (soft-delete) a walkbook
 */
export function useArchiveWalkbook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const api = getApiClient();
      return api.walkbooks.archiveWalkbook(id);
    },
    // no-data-door: the contact-rate overlay is not a defined query yet, so no write set reaches it
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.walkbooks.contactRates() }),
  });
}

/**
 * Assign volunteers to a walkbook
 */
export function useAssignVolunteers() {
  return useMutation({
    mutationFn: async ({ id, volunteerIds }: { id: string; volunteerIds: string[] }): Promise<AssignmentsResponse> => {
      const api = getApiClient();
      return api.walkbooks.assignVolunteers(id, { volunteerIds });
    },
  });
}

/**
 * Remove a volunteer assignment from a walkbook
 */
export function useRemoveAssignment() {
  return useMutation({
    mutationFn: async ({ walkbookId, volunteerId }: { walkbookId: string; volunteerId: string }): Promise<void> => {
      const api = getApiClient();
      return api.walkbooks.removeAssignment(walkbookId, volunteerId);
    },
  });
}

/**
 * Update walkbook fields (name today; polygon goes through useUpdatePolygon
 * because that endpoint also recomputes houses/voters and returns a different
 * shape).
 */
export function useUpdateWalkbook() {
  return useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const api = getApiClient();
      return api.walkbooks.updateWalkbook(id, { name, polygon: null });
    },
  });
}

/**
 * Update the polygon boundary of a walkbook
 */
export function useUpdatePolygon() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, polygon }: { id: string; polygon: string }): Promise<PolygonUpdateResponse> => {
      const api = getApiClient();
      return api.walkbooks.updatePolygon(id, { polygon });
    },
    // no-data-door: the contact-rate overlay is not a defined query yet, so no write set reaches it
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.walkbooks.contactRates() }),
  });
}

/**
 * Preview what a polygon would contain (houses/voters) before creating/saving.
 *
 * Uses a sequence guard so rapid calls (e.g. during polygon vertex drags that
 * emit many `draw.update` events) can't produce stale data: if an earlier
 * request resolves after a later one, its response is discarded. Without this,
 * the Save button (gated on `houseCount > 0`) could get stuck disabled even
 * though the current polygon contains houses.
 */
export function usePreviewPolygon() {
  const seqRef = useRef(0);
  const [data, setData] = useState<PolygonPreviewResponse | undefined>(undefined);
  const [isPending, setIsPending] = useState(false);

  const mutate = useCallback((polygon: string) => {
    const mySeq = ++seqRef.current;
    setIsPending(true);
    const api = getApiClient();
    api.walkbooks
      .previewPolygon({ polygon })
      .then((result) => {
        if (mySeq !== seqRef.current) return;
        setData(result);
        setIsPending(false);
      })
      .catch(() => {
        if (mySeq !== seqRef.current) return;
        setIsPending(false);
      });
  }, []);

  const reset = useCallback(() => {
    seqRef.current++;
    setData(undefined);
    setIsPending(false);
  }, []);

  return { mutate, reset, data, isPending };
}
